import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, UseFormReturn } from 'react-hook-form';

import { POPULATION_DENSITY_PACING_SEED_TYPE_LABEL } from '../../constants/lineItems';
import useConfirm from '../../hooks/useConfirm';
import { useConstants } from '../../hooks/useConstants';
import usePrevious from '../../hooks/usePrevious';
import { resolveSeedSecondaryKpi } from '../../util/kpiOptions';
import { Campaigns, LineItem } from '../../validationSchemas/LineItemSchema';
import LineItemKpiRow from './LineItemKPIRow';

interface LineItemKPIProps {
  hasPopulationDensityPacing: boolean;
  setPopulationDensityPacing: (value: boolean) => void;
  populationDensityPacingDisabledOptimizationsIds: number[];
}

function LineItemKPI({
  hasPopulationDensityPacing,
  setPopulationDensityPacing,
  populationDensityPacingDisabledOptimizationsIds,
}: LineItemKPIProps) {
  const {
    setValue,
    watch,
    getValues,
    formState: {
      defaultValues: { campaigns: defaultCampaigns, kpiGoal: defaultKpiGoal },
    },
  }: UseFormReturn<LineItem> = useFormContext();
  const emptyKpi = useMemo(() => ({ name: '', value: 0 }), []);
  const { fields, append, remove } = useFieldArray({ name: 'kpiObjectives' });
  const { windowDotConfirm } = useConfirm({
    title: 'Are you sure?',
    message: 'Enabling population density pacing will remove all KPIs.',
    confirmButtonText: 'Proceed',
  });

  const { constants } = useConstants();
  const CORE_KPI_SEED_TYPES = useMemo(
    () => constants?.INSERTION_ORDER?.SEED_TYPES || [],
    [constants?.INSERTION_ORDER?.SEED_TYPES]
  );
  const [enabledKpis, setEnabledKpi] = useState([]);
  const hasPopulationDensityPacingPreviously = usePrevious(hasPopulationDensityPacing);

  const optimizationTypeIds = watch('optimizationTypeIds');

  // Disable KPIs if population density is chosen.
  useEffect(() => {
    const confirmHelper = async () => {
      if (await windowDotConfirm()) {
        setPopulationDensityPacing(true);
      } else {
        setValue(
          'optimizationTypeIds',
          optimizationTypeIds.filter((id) => id !== 3)
        );
      }
    };

    if (
      optimizationTypeIds &&
      optimizationTypeIds.length > 0 &&
      optimizationTypeIds.includes(3) &&
      !hasPopulationDensityPacing
    ) {
      confirmHelper();
    }

    if (!optimizationTypeIds.includes(3) && hasPopulationDensityPacing) setPopulationDensityPacing(false);
  }, [optimizationTypeIds, windowDotConfirm, setPopulationDensityPacing, hasPopulationDensityPacing, setValue]);

  const watchedKpiObjectives = watch('kpiObjectives');
  const kpiObjectivesString = JSON.stringify(watchedKpiObjectives);

  useEffect(() => {
    if (!hasPopulationDensityPacingPreviously && hasPopulationDensityPacing) {
      // Remove all KPIs and add population density pacing
      setValue('kpiObjectives', [
        {
          name: POPULATION_DENSITY_PACING_SEED_TYPE_LABEL,
          // Use -1 as dummy data for population density pacing
          value: -1,
          type: 'coreKpi',
          adGroupIds: [],
        },
      ]);

      setValue(
        'optimizationTypeIds',
        optimizationTypeIds.filter((id) => !populationDensityPacingDisabledOptimizationsIds.includes(id))
      );
    }

    if (hasPopulationDensityPacingPreviously && !hasPopulationDensityPacing) {
      // Remove population density pacing
      setValue('kpiObjectives', [emptyKpi]);
    }
  }, [
    emptyKpi,
    hasPopulationDensityPacing,
    hasPopulationDensityPacingPreviously,
    optimizationTypeIds,
    populationDensityPacingDisabledOptimizationsIds,
    setValue,
  ]);

  // This is the core code that monitors all changes to the KPI objectives
  // and updates form values to be submitted to the server.
  // - kpiGoal
  // - scoreListMetric
  // - campaigns
  // - storeList
  // - conversionPixelUrl
  // - receiptProductList
  useEffect(() => {
    // We must initialize `campaigns` with all the original campaign IDs with an empty array.
    // If we remove all seeds type IDs from the UI, it will result in an empty array
    // to be saved and submitted on the server to clear the campaign of all seed type IDs.
    const campaigns: Campaigns = [
      ...defaultCampaigns.map((defaultCampaign) => ({
        ...defaultCampaign,
        seedTypeIds: [],
      })),
    ];

    const setValueOptions = { shouldValidate: true, shouldDirty: true };
    let disabledKpis = CORE_KPI_SEED_TYPES.map(([, label]) => label);
    const newEnabledKpis = [];

    const newKpiGoal: any = { secondaryKpi: resolveSeedSecondaryKpi(defaultKpiGoal) };
    setValue('scoredListMetric', 'Conversion', { shouldDirty: false });

    const kpiObjectives = (kpiObjectivesString && JSON.parse(kpiObjectivesString)) || [];
    kpiObjectives?.forEach((kpi) => {
      // Updates values for `kpiGoal`
      const enabledKpi = kpi.name;
      newEnabledKpis.push(enabledKpi);
      disabledKpis = disabledKpis.filter((item) => item !== enabledKpi);

      if (!newKpiGoal[kpi.type]) {
        newKpiGoal[kpi.type] = [];
      }
      // The lineItemSchema allows a number or string for the KPI goal value
      // but when we save, we prefer to store the KPI goal value as a string on the server.
      const newKpiGoalValue = { [kpi.name]: kpi.value?.toString() };

      // There is only one secondary KPI, so we always set it as an array with one object.
      // For primary KPIs, we will push them to the array.
      if (kpi.type === 'secondaryKpi') {
        newKpiGoal[kpi.type] = [newKpiGoalValue];
        // Updates values for `scoreListMetric`
        // Silently set the scoredListMetric without setting the form to dirty as it is hidden field
        setValue('scoredListMetric', kpi.name, { shouldDirty: false });
      } else {
        newKpiGoal[kpi.type].push(newKpiGoalValue);
      }

      // Update values for `campaigns`
      const adGroupIds = kpi.adGroupIds || [];
      const seedType = CORE_KPI_SEED_TYPES.find(([, label]) => label === kpi.name);
      if (seedType) {
        const seedTypeId = seedType[0];
        adGroupIds.forEach((adGroupId) => {
          const campaign = campaigns.find((campaignItem) => campaignItem.id === adGroupId);
          if (campaign) {
            campaign.seedTypeIds.push(seedTypeId);
          } else {
            campaigns.push({
              id: adGroupId,
              seedTypeIds: [seedTypeId],
            });
          }
        });
      }
      setValue('campaigns', campaigns, { shouldValidate: false, shouldDirty: false });

      // Need to set the initial values for storeList and receiptProductList to be empty arrays
      // or else it will be null and cause issues in the AutocompleteArray component
      // where typing the first character will always be reset to be empty.
      if (enabledKpi === 'Visits') {
        if (!getValues('visitStoreList.storeList')) {
          setValue('visitStoreList.storeList', [], { shouldDirty: false });
        }
      }
      if (enabledKpi === 'Receipts') {
        if (!getValues('receiptProductList.productList')) {
          setValue('receiptProductList.productList', [], { shouldDirty: false });
        }
        if (!getValues('receiptProductList.productLowerCategories')) {
          setValue('receiptProductList.productLowerCategories', [], { shouldDirty: false });
        }
      }
    });
    setValue('kpiGoal', newKpiGoal, setValueOptions);
    setEnabledKpi(newEnabledKpis);

    // Updates values to storeList, conversionPixelUrl, and receiptProduceList
    disabledKpis.forEach((disabledKpi) => {
      if (disabledKpi === 'Visits') {
        if (getValues('visitStoreList.id')) {
          setValue('visitStoreList._destroy', true, setValueOptions);
        } else {
          setValue('visitStoreList', null, setValueOptions);
        }
      }
      if (disabledKpi === 'Pixel Conversion') {
        if (getValues('conversionPixelUrl.id')) {
          setValue('conversionPixelUrl._destroy', true, setValueOptions);
        } else {
          setValue('conversionPixelUrl', null, setValueOptions);
        }
      }
      if (disabledKpi === 'Receipts') {
        if (getValues('receiptProductList.id')) {
          setValue('receiptProductList._destroy', true, setValueOptions);
        } else {
          setValue('receiptProductList', null, setValueOptions);
        }
      }
    });
  }, [CORE_KPI_SEED_TYPES, getValues, setValue, kpiObjectivesString, defaultCampaigns, defaultKpiGoal]);

  const fieldsWithoutPopulationDensityPacingFields = fields.filter(
    (field, index) => getValues(`kpiObjectives.${index}.name`) !== POPULATION_DENSITY_PACING_SEED_TYPE_LABEL
  );

  return (
    <>
      <Typography>KPI List</Typography>
      <Stack pr={2} spacing={1}>
        {fieldsWithoutPopulationDensityPacingFields.map((field, index) => (
          <LineItemKpiRow
            key={field.id}
            index={index}
            remove={remove}
            append={append}
            enabledKpis={enabledKpis}
            totalKpis={fieldsWithoutPopulationDensityPacingFields.length}
          />
        ))}
      </Stack>
      {fields.length <= 4 && (
        <Stack direction="row">
          <Button variant="text" onClick={() => append(emptyKpi)} disabled={hasPopulationDensityPacing}>
            <FontAwesomeIcon icon={faPlus} />
            &nbsp;Add KPI
          </Button>
        </Stack>
      )}
    </>
  );
}

export default LineItemKPI;
